import React, { FC, useState, useEffect } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Chip, Button, TablePagination } from '@material-ui/core';
import API from '../../services/ApiInstance';
import apiRoutes from '../../services/ApiRoutes';
import NewUserDialog from './NewUserDialog';
import EditUserDialog from './EditUserDialog';
import { PERMISSIONS, Roles } from '../../constants';
import { trackPage } from '../../services/analytics/AnalyticsService';
import { toast } from 'react-toastify';
import useAuthorization from '../../hooks/useAuthorisationManager';
import { UserDto } from '../../api/originalMappings';
import useLoggedInUser from '../../useLoggedInUser';
import UserPreferenceService from '../../services/UserPreferenceService';

const userPreferenceService = new UserPreferenceService();

const UserManagement: FC = () => {
  const [users, setUsers] = useState<UserDto[]>();
  const [roles, setRoles] = useState<Roles[]>();
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const { hasPermission } = useAuthorization();
  const { currentUser, setCurrentUser } = useLoggedInUser();

  useEffect(() => {
    async function OnInit() {
      trackPage('user management');
      try {
        await fetchUserList();
        await fetchRolesList();
      } catch (error) {
        console.log(error);
      }
    }
    OnInit();
  }, []);

  useEffect(() => {
    const savedRows = userPreferenceService.get('UserManagementRows');
    if (savedRows !== null) setRowsPerPage(savedRows);
    const savedPage = userPreferenceService.get('UserManagementPage');
    if (savedPage !== null) setPage(savedPage);
  }, []);

  useEffect(() => {
    userPreferenceService.save('UserManagementRows', rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    userPreferenceService.save('UserManagementPage', page);
  }, [page]);

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  async function fetchUserList() {
    const usersIn = await API.get<any, UserDto[]>(apiRoutes.userList);
    setUsers(usersIn);
  }

  async function fetchRolesList() {
    const rolesIn = await API.get<any, Roles[]>(apiRoutes.roles);
    setRoles(rolesIn);
  }

  async function onEdit() {
    toast.success('User updated');
    await fetchUserList();
  }

  async function onCreate() {
    await fetchUserList();
    toast.success('User created');
  }

  function canEditUser(user) {
    const roles = user.roleLevel > currentUser.userRole.level;
    const hasRolePermission = hasPermission(PERMISSIONS.User.Update);

    return roles && hasRolePermission;
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16 }}>
        {roles && <NewUserDialog roles={roles} onSuccess={onCreate} />}
      </div>
      <div className="page-card" style={{ overflowX: 'auto' }}>
        <Table style={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell>User Name</TableCell>
              <TableCell>Organisations</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Is Active</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users &&
              users
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow key={user.id}>
                  <TableCell component="th" scope="row">{user.username}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {user.organisations.slice(0, 3).map((org, idx) => (
                        <Chip key={`${org.id}-${idx}`} label={org.name} color="primary" size="small" />
                      ))}
                      {user.organisations.length > 3 && (
                        <Button color="primary" size="small">+ {user.organisations.length - 3}</Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.roleName}</TableCell>
                  <TableCell>{user.phone ?? ''}</TableCell>
                  <TableCell>{user.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    {user && canEditUser(user) && (
                      <EditUserDialog user={user} roles={roles} onSuccess={onEdit} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={users?.length ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </>
  );
};

export default UserManagement;
